import store from "@/store";
import Vue from "vue";
import { debounce, getSettingData, throttle } from "_lib/utils/utils";
import EventBus from '../EventBus'
import {
  RTC_TYPE,
  PHONE_MIC_ID,
  CHECK_USER_IN_ID_LIST,
  TRTC_DB_TO_VOLUME_TABLE,
  IS_USER_ADDITION,
  IS_USER_BGM,
} from '@/common/constants/rtc'
import {
  handleDevices,
  getRealRtcType,
  rtcJoinLog,
  isUserVolumeActive,
  isUnverifiedUser,
  getRuntimeParam,
  reportTime,
  getRtcLogPath,
} from './utils'
import { updateAccountSetting } from '_api/settings';
import { getAdditionToken } from '_api/room/index'
import rtcSdk from './sdk/rtcSdk'

class Agroa {
  constructor() {
    this.clientData = {} // rtc频道数据
    this._alreadyInChannelMembers = null // 进入频道时已经进入在频道里的用户，主播模式判断用
    this.init()
  }
  async init() {
    this.bindData()
    // 在defineProperty后赋值setting_data，直接更新vuex中的setting_data数据
    await this.initSetting()
  }
  async initRtc() {
    if (this.use_electron_trtc) {
      // 通过addon进行设备变化监听
      const throttled = throttle(this.devideChangeCallback.bind(this), 1000)
      rtcSdk.addElectronDeviceEvent(throttled)
    } else {
      // 通过web api进行设备变化监听
      rtcSdk.addWebDeviceEvent(this.devideChangeCallback.bind(this))
    }
    this.getAllDevices(null, true)
  }
  async initSetting() {
    store.dispatch("SET_SETTING_DATA_PARAM", await getSettingData());
  }
  // 加入频道
  async join(data, success_cb = () => { }, error_cb = () => { }) {
    if (!rtcSdk.RtcApi) {
      await this.setRtcApi('trtc')
    }

    rtcJoinLog(data, rtcSdk.RtcType, this.setting_data)
    this.clientData = data;
    if (window.client_env !== 'electron') {
      // 初始化声音音量监听
      rtcSdk.initLocalMicListener(this.micListerCallback.bind(this))
    } else {
      const logPathObj = await getRtcLogPath()
      Object.assign(data, {
        logPathObj,
      })
      // 客户端设置音量之前需要先创建实例
      await rtcSdk.createClient(data)
      // electron提前设置一下音量
      this.changeOperationMic()
      this.changeOperationVoice()
    }
    this.setMuted(this.setting_data.audio_press_mode, false)

    // 保存当前频道码率
    this.screenShareJoinCallback && this.screenShareJoinCallback(data.screen_share_user)
    this._alreadyInChannelMembers = data.members?.reduce((acc, member) => {
      acc[member.user_id] = 1
      return acc
    }, {})
    // 如果标记为已加入过频道，先断连
    if (rtcSdk.isRtcConnected) {
      await rtcSdk.leave();
      window.electronAPI && window.electronAPI.notifyTrack()
    }
    try {
      // 客户端在设置音量之前已经创建实例，此处只需要处理web端
      if (window.client_env !== 'electron') {
        await rtcSdk.createClient(data)
      }
      await rtcSdk.join(data)

      await this.createLocalTrack();
      this.changeOperationVoice()
      if (this.setting_data.curVoiceId) {
        await rtcSdk.setRemoteVoiceDevice(this.setting_data.curVoiceId);
      }
      success_cb && success_cb();
      this.startPreAudioRecordInterval()
      reportTime('start')
      window.electronAPI?.setUserAudioState({
        audio_av_line: rtcSdk.RtcType
      })
    } catch (e) {
      Vue.prototype.$toast.error("加入失败");
      error_cb && error_cb();
      console.log(e);
    }
  }
  // 离开频道
  async leave(cb, isSwitchRtc = false) {
    if (!rtcSdk.RtcApi) return
    if (window.client_env !== 'electron') {
      rtcSdk.destroyLocalMicListener()
    }
    if (!rtcSdk.isRtcConnected) {
      cb && cb();
      return
    }
    clientLog('rtc-leave', {
      isSwitchRtc,
      RtcType: rtcSdk.RtcType
    })

    await rtcSdk.destroyTrack();
    await this.changeBgmState(false) // 退出频道的同时关闭伴奏
    if(this.closeVoiceRecord) await this.closeVoiceRecord() // 退出频道同时关闭录音
    if(this.stopPreAudioRecordInterval && this._autoSplitAudioRecordInterval) {
      // 退出频道同时关闭预录制
      await this.stopPreAudioRecordInterval(true)
    }
    await rtcSdk.leave();

    this.clearScreenShare()
    reportTime('stop')
    if (!isSwitchRtc) {
      store.commit('SET_ANCHOR_MUTE_USER_IDS', {}) // 退出频道后清空主播模式禁言列表
      store.commit('SET_IS_USER_STOP_RECEIVE_SCREEN_SHARE', false) // 退出频道后清除关闭过屏幕共享标记
    }
    if (store.getters.is_phone_mic_connecting) { // 手机麦克风需要通知断开连接
      EventBus.$emit('Phone_Mic_Disconnect')
    }
    cb && cb();
    window.electronAPI && window.electronAPI.notifyTrack()
    console.log("client leaves channel success");
    window.electronAPI?.setUserAudioState({
      audio_av_line: null,
    })
    this.screenShareLeaveCallback && this.screenShareLeaveCallback()
    // 离开房间时取消系统声音弱化
    if (process.env.IS_CLIENT) {
      this.execSystemAudioAttenuation(false)
    }
  }
  async createLocalTrack(force) {
    try {
      await rtcSdk.createTrack(force, this.clientData, this.trackParams, this.setting_data)
    } catch (e) {
      if (this.setting_data.micDevices.length == 0) {
        Vue.prototype.$query({
          config: {
            title: '麦克风异常',
            desc: '未检测到麦克风, 请检查麦克风设备后刷新重试',
            confirm: {
              text: '确认',
            },
          }
        })
      } else {
        Vue.prototype.$toast.error("本地音轨创建失败, 请检查麦克风设备");
      }
      console.log(e);
    }
    setTimeout(() => {
      window.electronAPI && window.electronAPI.notifyTrack()
    }, 300)
  }

  // 获取所有麦克风扬声器设备
  async getAllDevices(cb, log) {
    try {
      let [micDevices, voiceDevices] = await rtcSdk.getAllDevices(this.use_electron_trtc)

      let originMic = this.setting_data.micDevices || [],
        originVoice = this.setting_data.voiceDevices || []

      // 获取默认麦克风
      let chooseMicDevice = handleDevices(micDevices, originMic, {
        id: this.setting_data.curMicId,
        name: this.setting_data.curMicName
      }, this._firstInitAllDevice)

      // 获取默认扬声器
      let chooseVoiceDevice = handleDevices(voiceDevices, originVoice, {
        id: this.setting_data.curVoiceId,
        name: this.setting_data.curVoiceName
      }, this._firstInitAllDevice)

      log && clientLog('get all devices', {
        micDevices,
        voiceDevices,
        RtcType: rtcSdk.RtcType,
        curMicId: chooseMicDevice.id,
        curMicName: chooseMicDevice.name,
        curVoiceId: chooseVoiceDevice.id,
        curVoiceName: chooseVoiceDevice.name,
      })

      store.dispatch("SET_SETTING_DATA_PARAM", {
        curMicId: chooseMicDevice.id,
        curMicName: chooseMicDevice.name,
        curVoiceId: chooseVoiceDevice.id,
        curVoiceName: chooseVoiceDevice.name,
        micDevices,
        voiceDevices,
      });
      // 有回调说明是 设备变化
      if (cb) {
        try {
          cb()
        } catch (e) {
          console.log(e)
        }
        if (!this.setting_data.never_show_devices_dialog) {
          setTimeout(() => {
            EventBus.$dynamic('DeviceChange')
          })
        }
      }
      this._firstInitAllDevice = true
    } catch (e) {
      // web没有麦克风权限时获取列表会报错 permission deny，此处不需要处理
      console.log(e)
    }

  }
  // 修改麦克风音量
  async changeOperationMic(v) {
    let d = v ?? this.setting_data.micLevel
    clientLog('rtc-change-mic-level', d)

    if (this.setting_data.curMicId === PHONE_MIC_ID) { // 手机麦克风通过electron通知手机端
      window.phoneMicAPI.updateMicLevel(d)
    }

    rtcSdk.setLocalVolume(d)
  }
  // 修改扬声器音量
  changeOperationVoice() {
    let v = this.clientData.isMuteEarphone ? 0 : this.setting_data.voiceLevel
    clientLog('rtc-change-voice-level', v)
    let res = rtcSdk.setPlayoutVolume(v)
    if (!res) {
      for (let uid in rtcSdk.getRemoteUsers()) {
        this.changeUserVolume(uid)
      }
    }
  }
  // 修改麦克风设备
  async changeMicDevice(id) {
    let device
    if (!id && this.setting_data.micDevices.length > 0) {
      let curDevice = this.setting_data.micDevices.find(v => v.deviceId === this.setting_data.curMicId)
      if (this.setting_data.curMicId && curDevice) {
        device = curDevice
        id = this.setting_data.curMicId
      } else {
        device = this.setting_data.micDevices[0]
        id = device.deviceId
      }
    } else {
      device = this.setting_data.micDevices.find(v => v.deviceId === id)
    }
    store.dispatch("SET_SETTING_DATA_PARAM", {
      curMicId: id,
      curMicName: device && device.label
    });

    try {
      clientLog('rtc-change-mic-device', id)
      this.setMicDevice(id)
    } catch (e) {
      Vue.prototype.$toast.error("麦克风设置失败");
      console.log(e);
    }
  }
  // 修改扬声器设备
  async changeVoiceDevice(id) {
    clientLog('rtc-change-voice-device', id)
    try {
      await rtcSdk.setRemoteVoiceDevice(id);
      let device = this.setting_data.voiceDevices.find(v => v.deviceId === id)
      store.dispatch("SET_SETTING_DATA_PARAM", {
        curVoiceId: id,
        curVoiceName: device && device.label
      });
    } catch (e) {
      Vue.prototype.$toast.error("扬声器设置失败");
      console.log(e);
    }
  }
  // 修改增强功能
  async changeAdvanceOperate(obj) {
    clientLog('rtc-change-advance-operate', obj)
    store.dispatch("SET_SETTING_DATA_PARAM", obj);

    rtcSdk.setRtc3A(this.trackParams, this.clientData, this.setting_data)
  }
  // 修改AI降噪功能
  changeAIDenoiser(v) {
    clientLog('rtc-change-ai-denoiser', v)
    store.dispatch("SET_SETTING_DATA_PARAM", {
      AI_denoiser_V2: v
    });
    // 只有trtc有
    return rtcSdk.setAIDenoiser(v) ?? true
  }

  // 修改单个用户的音量
  changeUserVolume(id, isSubscribeCb) {
    let member_voice_level = this.setting_data.member_voice_level
    let userVolume = member_voice_level[id] !== undefined
      ? member_voice_level[id]
      : 100

    // 主播模式逻辑处理
    let same_channel = CHECK_USER_IN_ID_LIST(id, this._alreadyInChannelMembers)
    let { anchor_mute_user_ids, anchor_mode, user_account_settings } = store.getters
    let isNeedAnchorMute = false // 是否需要对用户进行主播模式静音
    let isNeedUpdateMuteIds = false // 是否需要更新主播模式静音id列表
    let anchor_mute_user = anchor_mute_user_ids[id]
    const anchor_mode_mute_channel = user_account_settings.anchor_mode_mute_channel
    if (same_channel && !anchor_mute_user && !anchor_mode_mute_channel) { // 当前频道内用户，如果不是静音同频，标记为忽略
      let _anchor_mute_user_ids = JSON.parse(JSON.stringify(anchor_mute_user_ids))
      _anchor_mute_user_ids[id] = {
        user_id: id,
        same_channel,
        ignore: true,
      }
      store.commit('SET_ANCHOR_MUTE_USER_IDS', _anchor_mute_user_ids)
      anchor_mute_user_ids = _anchor_mute_user_ids
    }
    if (!(anchor_mute_user && anchor_mute_user.ignore)) { // 如果已经标记为忽略不进行主播模式音量处理
      if (store.getters.anchor_white_list?.some(user => user.user_id == id)) { // 如果在主播模式白名单里，不进行主播模式处理
      } else if (anchor_mode && anchor_mode_mute_channel && isSubscribeCb) { // 主播模式 & 静音同频道，只要是在同一个频道内订阅过的用户都主播静音
        isNeedAnchorMute = true
        isNeedUpdateMuteIds = true
      } else if (anchor_mode && !same_channel && isSubscribeCb) { // 主播模式，频道内新增用户主播静音
        isNeedAnchorMute = true
        isNeedUpdateMuteIds = true
      } else if (anchor_mode && anchor_mute_user && !anchor_mute_user.ignore) {
        // 在主播模式封禁列表中并且没标记为忽略的都进行主播静音（比如进入频道后设置静音同频道，
        // 频道内用户都在列表上，业务代码会重新对这些用户调用一次changeUserVolume）
        isNeedAnchorMute = true
      }
    }
    if (isNeedAnchorMute) { // 设置音量为0
      userVolume = 0
    }
    if (isNeedUpdateMuteIds) { // 更新主播静音列表
      let _anchor_mute_user_ids = JSON.parse(JSON.stringify(anchor_mute_user_ids))
      _anchor_mute_user_ids[id] = {
        user_id: id,
        same_channel,
      }
      store.commit('SET_ANCHOR_MUTE_USER_IDS', _anchor_mute_user_ids)
    }
    if (this._alreadyInChannelMembers) { // 进入频道后触发完已经在频道里的用户主播模式逻辑后 需要清空_alreadyInChannelMembers字段
      this.clearInChannelMember()
    }

    if (store.getters.bilibili_living && isUnverifiedUser(id)) {
      userVolume = 0
    }

    rtcSdk.setUserPlayoutVolume(id, userVolume, this.clientData, this.setting_data)
  }
  // 按键麦静音/开声音方法
  setMuted(bl, showTip = true) {
    clientLog('rtc-set-mute', bl)
    rtcSdk.setMuted(bl)
    clearTimeout(this._setMutedTimeout)
    if (bl) {
      this._setMutedTimeout = setTimeout(() => {
        this.setMuted(bl)
      }, 1000 * 60)
    } else {
      if (showTip && this.setting_data.micLevel === 0 && rtcSdk.isRtcConnected) {
        EventBus.$emit('User_Speak_Without_Voice', true)
      }
    }
  }
  // 语音禁用状态改变
  async setMuteBanned(v) {
    clientLog('rtc-mute-banned', v)
    this.clientData.isMuteBanned = v

    if (v) {
      try {
        await rtcSdk.unpublish()
      } catch (e) {
        console.log(e)
      }
    } else {
      try {
        await this.createLocalTrack(true)
      } catch (e) {
        console.log(e)
      }
    }
  }
  // 耳机禁用状态改变
  async setEarphoneBanned(v) {
    clientLog('rtc-earphone-banned', v)
    this.clientData.isMuteEarphone = v
    this.changeOperationVoice()
  }

  // 设置rtc版本
  async setRtcApi(type = 'trtc', preventDeviceRefresh = false) {
    let realType = getRealRtcType(type, this.use_electron_trtc)
    clientLog('set-rtc-api', { type: realType, preventDeviceRefresh })
    let res = await rtcSdk.setRtcApi(realType)
    this.registerEvent()

    if (res?.rtcRequireEnvChange && !preventDeviceRefresh) { // electron环境的设备列表和web环境不同，切换环境要更新设备列表
      await this.getAllDevices()
    }
  }
  // 尝试重置并切换trtcsdk（在需要进行重置的前提下）
  async resetTrtcSdk(resetToken) {
    if (rtcSdk.RtcType && !rtcSdk.RtcType.includes('volc')) return // 如果当前所使用的sdk是volc的。不进行处理

    if (rtcSdk.RtcType) {
      // 如果已经加载了trtc sdk，尝试进行sdk切换
      let newRtcType = await getRealRtcType('trtc', this.use_electron_trtc)
      if (newRtcType === rtcSdk.RtcType) return

      EventBus.$emit('Reset-Trtc-Sdk')
      await this.closeTestTrack()
      let isConnceted = rtcSdk.isRtcConnected
      if (isConnceted) {
        EventBus.$emit('Clear_System_Bgm_state')
        await this.leave(null, true);
      }
      await rtcSdk.setRtcApi(newRtcType)
      this._firstInitAllDevice = false // 更新首次初始化设备标记，防止识别所有设备都是新设备
      await this.getAllDevices()
      if (isConnceted) {
        if (resetToken && this.clientData?.room_id && this.clientData?.channel_id) {
          let res = await getAdditionToken({
            room_id: this.clientData.room_id,
            channel_id: this.clientData.channel_id,
            token_type: 'audio',
          })
          if (res.data.status === 'ok') {
            this.clientData = {
              ...this.clientData,
              ...res.data.result.res
            }
          }
        }
        await this.join(this.clientData)
      }
    } else {
      // 如果当前没有加载sdk，重置设备变化监听并重新获取设备列表
      rtcSdk.removeWebDeviceEvent()
      rtcSdk.removeElectronDeviceEvent()
      this._firstInitAllDevice = false // 更新首次初始化设备标记，防止识别所有设备都是新设备
      this.initRtc()
    }


  }

  // 注册回调（订阅回调，网络状况检测）
  registerEvent() {
    if (this._registerEventInited) return
    this._registerEventInited = true
    rtcSdk.removeWebDeviceEvent()
    rtcSdk.removeElectronDeviceEvent()
    EventBus.$on('rtc-cb:subscribeCallback', this.subscribeCallback.bind(this))
    EventBus.$on('rtc-cb:devideChangeCallback', this.devideChangeCallback.bind(this))
    EventBus.$on('rtc-cb:volumeIndicatorCallback', this.volumeIndicatorCallback.bind(this))
    EventBus.$on('rtc-cb:permissionDenyCallback', this.permissionDenyCallback.bind(this))
    EventBus.$on('rtc-cb:connectionStateChangeCallback', this.connectionStateChangeCallback.bind(this))
    EventBus.$on('rtc-cb:bgmErrorCallback', this.bgmErrorCallback.bind(this))
    EventBus.$on('rtc-cb:bgmWarnCallback', this.bgmWarnCallback.bind(this))
    EventBus.$on('rtc-cb:recreateTrackCallback', this.recreateTrackCallback.bind(this))

    this.screenShareRegisterEvent && this.screenShareRegisterEvent()
  }
  async setMicDevice(id) {
    if (this._setMicDeviceLock) return
    this._setMicDeviceLock = true

    clientLog('rtc-change-mic-device', id)
    store.dispatch("SET_SETTING_DATA_PARAM", {
      curMicId: id
    });

    try {
      await rtcSdk.setMicDevice(id);
    } catch (e) {
      console.log(e)
      if (!this.clientData.isMuteBanned) {
        await rtcSdk.publish(this.trackParams);
      }
    }
    this._setMicDeviceLock = false
  }
  micListerCallback(level) {
    if (level > 5 && this.setting_data.micLevel === 0) {
      EventBus.$emit('User_Speak_Without_Voice')
    }
  }

  // 调试track方法
  async createTestTrack(cb, playback = true) {
    // 调试麦克风时如果没进入过语音频道，rtc sdk会未引入，
    // 需要在此处引入默认的trtc web sdk
    if (!rtcSdk.RtcApi) {
      await this.setRtcApi('trtc', true)
    }
    clientLog('rtc-create-test-mic', rtcSdk.RtcApi.RtcType, this.trackParams, playback)
    rtcSdk.createTestTrack(cb, playback, this.trackParams, this.setting_data)

    setTimeout(() => {
      window.electronAPI && window.electronAPI.notifyTrack()
    }, 300)
  }
  async closeTestTrack(playback = true) {
    clientLog('rtc-close-test-mic')
    rtcSdk.closeTestTrack(playback)
  }

  // 切换伴奏状态
  async changeBgmState(bl, path) {
    if (!rtcSdk.RtcApi || window.client_env !== 'electron') return // web没有伴奏功能
    clientLog('rtc-bgm-state', bl, path)
    if (bl) {
      EventBus.$emit('Phone_Mic_Disconnect')
    }
    EventBus.$emit('Add_Room_State_Number', this.cur_channel_data.room_id, 'bgm', bl);
    rtcSdk.changeSystemAudioLoopback(bl, path, this.setting_data.bgm_volume)
  }
  // 切换伴奏音量
  async changeBgmVolume(v) {
    clientLog('rtc-bgm-volume', v)
    rtcSdk.changeSystemAudioLoopbackVolume(v)
  }

  clearInChannelMember = debounce(function () {
    this._alreadyInChannelMembers = null
  }, 150)

  async handleChangeClearMode(v) {
    if (this._changeElectronSdkLock) return
    this._changeElectronSdkLock = true
    store.dispatch('SET_USER_ACCOUNT_SETTINGS', {
      audio_clear_mode: v,
    })
    updateAccountSetting({}, {
      setting: 'audio_clear_mode',
      value: v
    })
    await this.resetTrtcSdk(true)
    this._changeElectronSdkLock = false
  }

  async changeRtcPlugin(config) {
    clientLog('rtc-plugin', config)
    rtcSdk.changeRtcPlugin(config)
  }

  // 判断是否需要调用系统声音弱化功能
  checkSystemAudioAttenuation(volumes) {
    if (window.client_env !== 'electron') return
    if (!this.getIsActiveSystemAudioAttenuation()) return
    let reasons = this.getUserVolumeActiveType(volumes)
    let is_self_active = this.setting_data.system_audio_attenuation_when_self && reasons.self
    let is_other_active = this.setting_data.voiceLevel && !this.clientData.isMuteEarphone && this.setting_data.system_audio_attenuation_when_other && reasons.other
    if (is_self_active || is_other_active) {
      this.execSystemAudioAttenuation(true)
    } else {
      this.execSystemAudioAttenuation(false)
    }
  }

  // 判断系统声音弱化功能是否启用
  getIsActiveSystemAudioAttenuation() {
    return this.setting_data.system_audio_attenuation < 100
      && (this.setting_data.system_audio_attenuation_when_self
        || this.setting_data.system_audio_attenuation_when_other)
  }

  // 启动|取消系统声音弱化
  execSystemAudioAttenuation(v) {
    let level = this.setting_data.system_audio_attenuation
    // system_audio_attenuation 100说明不需要进行弱化
    if (v && level === 100) return

    // 不能连续启用或取消系统声音弱化
    if (v === this._execSystemAudioAttenuationLock) {
      return
    } else {
      this._execSystemAudioAttenuationLock = v
    }
    clientLog('execSystemAudioAttenuation', v, level)
    window.electronAPI.systemAudioAttenuation(v, level)
  }
  // 获取自己或者其他人的麦克风状态
  getUserVolumeActiveType(volumes) {
    let result = {
      self: undefined,
      other: false,
    }
    for (let v of volumes) {
      let isActive = isUserVolumeActive(v.level)
      let isCommonMic = !IS_USER_ADDITION(v.uid) && !IS_USER_BGM(v.uid) // 用户麦克风推流，不是屏幕共享或伴奏
      if (v.uid == this.user_info.user_id) {
        result.self = isActive
      } else if (isCommonMic && !result.other && isActive) {
        result.other = true
      }
      if (result.self !== undefined && result.other) {
        return result
      }
    }
    return result
  }
  getLocalVolumePercent(volume) {
    let base = 100
    if (rtcSdk.RtcType === RTC_TYPE.TRTC_ELECTRON) {
      for (let i = 0; i <= 100; i++) {
        let table_volume = TRTC_DB_TO_VOLUME_TABLE[i]
        if (volume <= table_volume) {
          return Math.max(i - 1, 0)
        }
      }
      return 100
    } else {
      return Math.min(Math.floor(volume / base * 100), 100)
    }
  }


  subscribeCallback(user) {
    rtcSdk.setRemoteVoiceDevice(this.setting_data.curVoiceId, user)
    this.changeUserVolume(user.uid, true)
  }

  devideChangeCallback(isCb) {
    if (isCb === false) { // isCb = false 说明是rtc.js内调用更新设备列表
      this._firstInitAllDevice = false
      this.getAllDevices(null, false)
      return
    } else {
      this.getAllDevices(async () => {
        if (this.setting_data.curVoiceId) {
          await this.changeVoiceDevice(this.setting_data.curVoiceId)
        }
        if (this.setting_data.curMicId) {
          await this.setMicDevice(this.setting_data.curMicId);
        }
        console.log("devide callback")
      }, true)
    }
  }

  volumeIndicatorCallback(volumes) {
    let user_volumes = {};
    for (let v of volumes) {
      if (v.uid) {
        user_volumes[v.uid] = v.level;
      }
    }
    this.checkSystemAudioAttenuation(volumes)
    // 必须单独触发需要更新音量状态的用户， 否则全量更新会导致频道内用户越多内存影响越大
    for (let id in user_volumes) {
      if (store.state.settings.user_volumes[id] != user_volumes[id]) {
        Vue.set(store.state.settings.user_volumes, id, user_volumes[id])
      }
    }
    let del_keys = Object.keys(store.state.settings.user_volumes).filter(id => user_volumes[id] === undefined)
    del_keys.forEach((id) => {
      Vue.delete(store.state.settings.user_volumes, id)
    })
  }

  // 没有麦克风权限提示 (仅web环境会调用)
  permissionDenyCallback() {
    Vue.prototype.$query({
      config: {
        title: '当前浏览器未授权麦克风权限，无法使用麦克风',
        desc: '开启麦克风权限后刷新重试，不同浏览器开启权限方式见<a href="https://chat.xiaoheihe.cn/home/help-center/detail/1760208687768264704" target="_blank" style="color: var(--brand-text)">帮助中心</a>',
        confirm: {
          text: '我知道了',
        },
      },
    })
  }

  connectionStateChangeCallback(v) {
    store.commit('SET_RCT_CONNECTING', v)
  }

  bgmErrorCallback() {
    if (!store.getters.my_screen_sharing) {
      this.changeBgmState(false)
      EventBus.$emit('Clear_System_Bgm_state')
    }
  }

  bgmWarnCallback(type) {
    EventBus.$emit('Bgm_Use_Hook_Warn', type)
  }

  async recreateTrackCallback() {
    clientLog('[rtc] recreateTrackCallback')
    try {
      await rtcSdk.createTrack(false, this.clientData, this.trackParams, this.setting_data)
    } catch (e) {
      console.log(e)
    }
  }

  isSdkCompany(type) {
    return rtcSdk.RtcType.includes(type)
  }

  bindData() {
    Object.defineProperty(this, 'setting_data', {
      get: function () {
        return store.getters.setting_data
      },
    })
    Object.defineProperty(this, 'use_electron_trtc', {
      get: function () {
        return process.env.IS_CLIENT && store.getters.user_account_settings.audio_clear_mode == 1
      },
    })
    Object.defineProperty(this, 'user_info', {
      get: function () {
        return store.getters.user_info
      },
    })
    Object.defineProperty(this, 'cur_channel_data', {
      get: function () {
        return store.getters.cur_channel_data
      },
    })
    Object.defineProperty(this, 'trackParams', {
      get: function () {
        return {
          AEC: this.setting_data.AEC || false,
          AGC: this.setting_data.AGC || false,
          ANS: this.setting_data.ANS || false,
          userId: this.user_info.user_id,
          AI_denoiser: this.setting_data.AI_denoiser_V2 || false,
          audio_bitrate: this.clientData?.audio_bitrate || 128,
          microphoneId: this.setting_data.curMicId,
          runtime_parameter: getRuntimeParam(rtcSdk.RtcType),
          trtc_ai_denoise_version: store.getters.user_account_settings.trtc_ai_denoise_version,
          trtc_ai_denoise_type: store.getters.user_account_settings.trtc_ai_denoise_type,
          mic_input_threshold: store.getters.user_account_settings.open_mic_input_threshold ? this.setting_data.mic_input_threshold : false,
        }
      },
    })
    Object.defineProperty(this, 'RtcType', {
      get: function () {
        return rtcSdk.RtcType
      },
    })
    this.screenShareBindDataCallback && this.screenShareBindDataCallback()
  }

  // 处理主持人数组变化事件
  async handleHostModeBannedChange(v) {
    clientLog('rtc-host-mode-banned', v)
    // 如果没有开启主持人模式，则不处理
    this.clientData.isHostModeBanned = v
    rtcSdk.changeHostModeBanned(v)

    if (v) {
      try {
        // 下麦时 同时关闭伴奏、语音包选择器、屏幕共享、录制
        await this.changeBgmState(false)
        store.commit('SET_BGM_STATE', {})
        EventBus.$emit('SHOW_LAUHGTER_SELECTOR', false)
        EventBus.$emit('STOP_SCREEN_SHARE')
        if(this.closeVoiceRecord) await this.closeVoiceRecord()
        

        await rtcSdk.unpublish()
      } catch (e) {
        console.log(e)
      }
    } else {
      try {
        await this.createLocalTrack(true)
      } catch (e) {
        console.log(e)
      }
    }
  }
}

export default Agroa;
